/**
 * useFormData Hook
 * Handles fetching and managing traveler form data
 */

import { useState, useRef, useCallback } from "react";
import { FormReviewData, Destination } from "@/types";
import { travelerService } from "@/services/travelers";

export interface UseFormDataOptions {
  travelerId: string | undefined;
  isViewMode: boolean;
  onDestinationsLoaded?: (destinations: Destination[]) => void;
  onRecommendationsNeeded?: (travelerId: string) => void;
}

export interface UseFormDataReturn {
  formData: FormReviewData | null;
  loading: boolean;
  error: string;
  fetchTravelerForm: () => Promise<void>;
}

export function useFormData({
  travelerId,
  isViewMode,
  onDestinationsLoaded,
  onRecommendationsNeeded,
}: UseFormDataOptions): UseFormDataReturn {
  const [formData, setFormData] = useState<FormReviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const hasFetchedRef = useRef(false);

  const startReview = useCallback(async () => {
    if (!travelerId) return;
    try {
      await travelerService.startReview(travelerId);
    } catch (err: any) {
      // Silent fail - review may already be started
    }
  }, [travelerId]);

  const fetchTravelerForm = useCallback(async () => {
    if (!travelerId) return;

    try {
      const data = await travelerService.getTravelerForm(travelerId);
      setFormData(data);

      // Notify parent about destinations for health info fetching
      if (data.tripDetails?.destinations && onDestinationsLoaded) {
        onDestinationsLoaded(data.tripDetails.destinations);
      }

      // Start review if form was just submitted
      if (data.formStatus === "submitted" && !isViewMode) {
        await startReview();
      }

      // Notify parent to fetch AI recommendations
      if (onRecommendationsNeeded) {
        onRecommendationsNeeded(travelerId);
      }
    } catch (err: any) {
      setError("Failed to load traveler form");
    } finally {
      setLoading(false);
    }
  }, [travelerId, isViewMode, onDestinationsLoaded, onRecommendationsNeeded, startReview]);

  return {
    formData,
    loading,
    error,
    fetchTravelerForm,
  };
}
